/* ==========================================================================
   social-proof.js — randomized "recent activity" popup, with sound.
   Content is synthetic (no real purchase data behind it). The tag line
   under the message ("The client will be notified when you like.") is
   set in sections/social-proof-popup.liquid — keep both in sync if it changes.

   The chime is synthesized at runtime with the Web Audio API (a couple of
   short sine-wave tones) rather than an embedded audio file — that sidesteps
   licensing entirely (there is no recording to license: nothing is played
   back, it's generated on the fly) while staying short, soft, and friendly.
   ========================================================================== */
(function () {
  "use strict";

  // Belt-and-suspenders: the Blog templates never include this section or
  // this script at all (see layout/theme.liquid's template.name check) —
  // that's the real fix. This is a second, independent guard so that even a
  // stray cached copy of the page can never play a sound or show the popup
  // while the URL is inside /blogs/.
  if (/^\/blogs(\/|$)/.test(window.location.pathname)) return;

  // Guards against this ever running twice on one page (e.g. a template
  // accidentally including the section more than once) — without this,
  // two copies of the script would each run their own schedule loop and
  // the popup would appear (and play sound) roughly twice as often as intended.
  if (window.__apykidzSocialProofInit) return;
  window.__apykidzSocialProofInit = true;

  var toast = document.querySelector("[data-social-proof]");
  if (!toast) return;

  var textEl = toast.querySelector("[data-social-proof-text]");
  var likeBtn = toast.querySelector("[data-social-proof-like]");
  var closeBtn = toast.querySelector("[data-social-proof-close]");
  var muteBtn = toast.querySelector("[data-social-proof-mute]");
  var muteIcon = toast.querySelector("[data-social-proof-mute-icon]");
  var hideTimer = null;
  var scheduleTimerId = null;
  // In-memory only, on purpose — resets to unmuted on every page load
  // (refresh or navigating to a new page). Never written to localStorage,
  // sessionStorage, or a cookie, per spec.
  var muted = false;

  var FIRST_DELAY_MS = 30 * 1000;  // fixed 30s for the very first popup after a page load
  var MIN_DELAY_MS = 30 * 1000;    // 30s — minimum gap AFTER the popup has fully disappeared
  var MAX_DELAY_MS = 240 * 1000;   // 240s (4 min) — maximum gap after it disappears
  var VISIBLE_MS = 10 * 1000;      // ~10s on screen
  var CHIME_TOTAL_MS = 450;        // matches the longest note's start+duration below, with margin

  /* ---------- Soft two-tone chime (Web Audio API, no audio file) ---------- */
  var audioCtx = null;
  var audioUnlocked = false;
  var chimeInFlight = false; // guards against two chimes overlapping/duplicating

  function getAudioCtx() {
    if (audioCtx) return audioCtx;
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    try { audioCtx = new AC(); } catch (e) { audioCtx = null; }
    return audioCtx;
  }

  function unlockAudio() {
    if (audioUnlocked) return;
    var ctx = getAudioCtx();
    if (ctx && ctx.state === "suspended") {
      ctx.resume().catch(function () {});
    }
    audioUnlocked = true;
  }
  // Browsers require a user gesture before audio can actually play — this
  // arms it on the visitor's very first interaction, anywhere on the site.
  ["click", "touchstart", "keydown"].forEach(function (evt) {
    document.addEventListener(evt, unlockAudio, { once: true, passive: true });
  });

  function playChimeNow() {
    if (muted || chimeInFlight) return; // "only once per notification"
    var ctx = getAudioCtx();
    if (!ctx) return; // Web Audio unsupported — fail silently, no sound, no error
    try {
      if (ctx.state === "suspended") ctx.resume().catch(function () {});
      chimeInFlight = true;
      setTimeout(function () { chimeInFlight = false; }, CHIME_TOTAL_MS);

      var now = ctx.currentTime;
      // A short, gentle two-note "ding-ding" — sine waves keep it soft
      // rather than buzzy, and the gain envelope avoids any harsh clicks.
      var notes = [
        { freq: 987.77, start: 0.0, dur: 0.16, peak: 0.16 },  // B5
        { freq: 1318.51, start: 0.11, dur: 0.3, peak: 0.14 }, // E6
      ];
      notes.forEach(function (n) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = n.freq;
        gain.gain.setValueAtTime(0.0001, now + n.start);
        gain.gain.exponentialRampToValueAtTime(n.peak, now + n.start + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + n.start + n.dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + n.start);
        osc.stop(now + n.start + n.dur + 0.02);
      });
    } catch (e) {
      chimeInFlight = false;
      /* Autoplay blocked or unsupported — the popup still shows visually,
         it just plays without sound. Never let this break the page. */
    }
  }

  function randomLetter() {
    var letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    return letters[Math.floor(Math.random() * letters.length)];
  }

  // Weighted so the amount reads as "$X**" most of the time — 60% two
  // asterisks, 35% one, only 5% three.
  function randomStarCount() {
    var r = Math.random();
    if (r < 0.60) return 2;   // 60%: $X**
    if (r < 0.95) return 1;   // 35%: $X*
    return 3;                 // 5%:  $X***
  }

  function randomMessage() {
    var name = randomLetter() + "*****";
    var digit = 1 + Math.floor(Math.random() * 9); // 1-9
    var amount = "$" + digit + "*".repeat(randomStarCount()) + "." + "*".repeat(2);
    return name + " just invested " + amount + " in the happiness of their kid(s).";
  }

  function showToast() {
    if (textEl) textEl.textContent = randomMessage();
    toast.classList.remove("is-visible"); // reset in case it's mid-transition
    if (likeBtn) likeBtn.classList.remove("liked");
    // Force reflow so re-adding the class always restarts the entrance transition,
    // even if a previous notification is still closing.
    void toast.offsetWidth;
    toast.classList.add("is-visible");

    // Synchronizing the chime with the animation: calling playChimeNow()
    // in this same tick would fire the sound before the browser has even
    // painted the class change, so it plays while the popup is still at
    // opacity:0 — audibly "before" anything is visible. A double
    // requestAnimationFrame guarantees one frame has been committed with
    // the new (entrance-transition-triggering) state before the chime
    // fires, so the sound lands right as the popup actually starts
    // animating in, instead of a beat before it.
    //
    // requestAnimationFrame is paused while the tab is backgrounded/hidden,
    // which would otherwise let the chime get silently dropped if a
    // scheduled notification happens to fire while the tab isn't in the
    // foreground. `firedSync` + the setTimeout fallback guarantee the sound
    // still plays in that case (just without the frame-perfect sync, which
    // doesn't matter when nothing is being watched anyway) — and guarantee
    // it fires exactly once either way.
    var firedSync = false;
    function fireSyncedChime() {
      if (firedSync) return;
      firedSync = true;
      playChimeNow();
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(fireSyncedChime);
    });
    setTimeout(fireSyncedChime, 100);

    clearTimeout(hideTimer);
    hideTimer = setTimeout(hideToast, VISIBLE_MS);
  }

  function hideToast() {
    clearTimeout(hideTimer);
    toast.classList.remove("is-visible");
    // The countdown to the *next* appearance starts here — right as this one
    // finishes disappearing — not back when it appeared. This also covers a
    // visitor manually dismissing it early via the close button: dismissing
    // counts as "disappeared", so the next random wait starts immediately.
    scheduleNext();
  }

  function scheduleNext() {
    clearTimeout(scheduleTimerId); // never let two of these stack
    var delay = MIN_DELAY_MS + Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS);
    scheduleTimerId = setTimeout(showToast, delay);
  }

  // If the page is navigated away from (including into the back/forward
  // cache), cancel any pending timers so nothing from this page instance
  // can ever fire a sound or popup later while a different page is showing.
  window.addEventListener("pagehide", function () {
    clearTimeout(scheduleTimerId);
    clearTimeout(hideTimer);
  });

  if (closeBtn) closeBtn.addEventListener("click", hideToast);
  if (muteBtn) {
    muteBtn.addEventListener("click", function () {
      muted = !muted;
      muteBtn.setAttribute("aria-pressed", muted ? "true" : "false");
      var label = muted ? "Unmute notification sound" : "Mute notification sound";
      muteBtn.setAttribute("aria-label", label);
      muteBtn.title = label;
      if (muteIcon) muteIcon.textContent = muted ? "🔇" : "🔊";
    });
  }
  if (likeBtn) {
    likeBtn.addEventListener("click", function () {
      likeBtn.classList.toggle("liked");
    });
  }

  // The very first popup after a fresh page load is fixed at 30s — only the
  // *repeat* appearances (scheduled from hideToast, once one has fully
  // disappeared) use the random 30–240s window.
  scheduleTimerId = setTimeout(showToast, FIRST_DELAY_MS);

  /* Exposed for manual QA (e.g. from the console) — mirrors the
     window.ApykidzCart pattern used elsewhere in this codebase. */
  window.ApykidzSocialProof = { show: showToast, hide: hideToast, playChime: playChimeNow };
})();
